import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Circular avatar with camera-button overlay for image selection.
 * Shows a data-URL preview immediately on pick; emits the raw File
 * for the parent to upload (e.g. to Firebase Storage).
 *
 * @input imageUrl  - Remote URL to display (e.g. photoURL from Firebase).
 * @input initials  - Fallback text shown when no image is available.
 * @output fileSelected - Emits the selected File so the parent can upload it.
 */
@Component({
  selector: 'om-avatar-upload',
  imports: [TranslateModule],
  templateUrl: './avatar-upload.html',
  styleUrl: './avatar-upload.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarUpload {
  imageUrl = input<string | null>(null);
  initials = input<string>('?');

  fileSelected = output<File>();
  photoRemoved = output<void>();

  /** Immediate local preview before the parent finishes uploading. */
  readonly previewUrl = signal<string | null>(null);
  private readonly _removed = signal(false);

  /** Priority: removed → null, else local preview → remote URL → null. */
  readonly displayUrl = computed<string | null>(() =>
    this._removed() ? null : (this.previewUrl() ?? this.imageUrl())
  );

  /** True when a photo is visible (either preview or remote). */
  readonly hasPhoto = computed(() => !!this.displayUrl());

  @ViewChild('fileInput') private fileInputRef!: ElementRef<HTMLInputElement>;

  openPicker(): void {
    this.fileInputRef.nativeElement.click();
  }

  onFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this._removed.set(false);
    const reader = new FileReader();
    reader.onload = (e) => this.previewUrl.set(e.target?.result as string);
    reader.readAsDataURL(file);

    this.fileSelected.emit(file);
  }

  /** Removes the current photo and emits photoRemoved. */
  removePhoto(): void {
    this.clearPreview();
    this._removed.set(true);
    this.photoRemoved.emit();
  }

  /** Clears preview and file input (e.g. after form reset). */
  clearPreview(): void {
    this.previewUrl.set(null);
    this._removed.set(false);
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }
}
